import { getNewSignupUrl, navigateTo } from 'lib/utils';
import * as analytics from 'lib/analytics';
import featureFlags from 'lib/featureFlags';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './AuthContent.module.css';
import SignupFields from './SignupFields';
import { sanitizeRedirect, buildAuthLink } from './redirectUtils';

type SignupContentProps = {
  sessionId?: string;
  serverError?: string;
};

const SignupContent: FunctionComponent<SignupContentProps> = ({
  sessionId,
  serverError,
}) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [optInToEmails, setOptInToEmails] = useState(true);
  const [errorString, setErrorString] = useState<string | null | undefined>(
    serverError
  );

  useEffect(() => {
    setErrorString(serverError);
  }, [serverError]);

  const router = useRouter();
  const { NEXT_PUBLIC_API: domain } = process.env;
  const referer = sanitizeRedirect(router?.query?.redirect);
  const authSessionId = useMemo(
    () => sessionId ?? analytics.generateSessionId(),
    [sessionId]
  );
  const firstEngagementFiredRef = useRef(false);

  useEffect(() => {
    if (
      firstEngagementFiredRef.current ||
      (email.length <= 0 && password.length <= 0)
    ) {
      return;
    }

    firstEngagementFiredRef.current = true;
    analytics.signupFirstEngagement({ session_id: authSessionId });
  }, [authSessionId, email, password]);

  function onGoogleSignup() {
    const params: Record<string, string> = {
      onlySignin: 'false',
      redirect: referer || getNewSignupUrl('google'),
      opt_in_to_emails: String(optInToEmails),
    };

    const paramsStr = new URLSearchParams(params).toString();
    const url = `${domain}/oauth/google/start?${paramsStr}`;
    analytics.signupOauthClicked({
      session_id: authSessionId,
      provider: 'google',
      opt_in_to_emails: optInToEmails,
      redirect_target: params.redirect,
    });
    navigateTo(url);
  }

  function onAppleSignup() {
    const params: Record<string, string> = {
      onlySignin: 'false',
      redirect: referer || getNewSignupUrl('apple'),
      opt_in_to_emails: String(optInToEmails),
    };

    const paramsStr = new URLSearchParams(params).toString();
    const url = `${domain}/oauth/apple/start?${paramsStr}`;
    analytics.signupOauthClicked({
      session_id: authSessionId,
      provider: 'apple',
      opt_in_to_emails: optInToEmails,
      redirect_target: params.redirect,
    });
    navigateTo(url);
  }

  return (
    <>
      <h1 className={styles.Heading}>Create account</h1>
      <div className={styles.SocialLoginGroup}>
        <div className={styles.SocialLogin}>
          <div onClick={onGoogleSignup}>
            Continue with Google
            <img alt="Google" src="/static/signin-google.png" />
          </div>
        </div>
        {featureFlags.appleSignin() && (
          <div className={styles.SocialLogin}>
            <div onClick={onAppleSignup}>
              Continue with Apple
              <img alt="Apple" src="/static/signin-apple.png" />
            </div>
          </div>
        )}
      </div>
      <div className={styles.OrDivider}>or</div>
      <SignupFields
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        optInToEmails={optInToEmails}
        setOptInToEmails={setOptInToEmails}
        sessionId={authSessionId}
        errorString={errorString}
        setErrorString={setErrorString}
      />
      <div className={styles.SwitchFlow}>
        Already have an account?{' '}
        <Link
          href={buildAuthLink('/login', referer)}
          onClick={() =>
            analytics.authSwitchToLoginClicked({
              session_id: authSessionId,
              from_page: 'signup',
            })
          }
        >
          Log in
        </Link>
      </div>
    </>
  );
};

export default SignupContent;
